/**
 * Admin Leads Console (Slice D — Feb 2026 July).
 *
 * Operational surface for the AWARE admin team to see every quote lead
 * across the network — including the ones sitting in the review queue
 * because postcode / attribution didn't resolve to a single partner.
 *
 *   • KPI strip with total, review-required, and per-status counts
 *   • Filters: status, partner, needs_admin_review, search
 *   • CSV export
 *   • Row → drawer to reassign a partner, add notes, change status,
 *     mark outside service area, and view the full audit history
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Search, Inbox, AlertTriangle, ChevronRight, Users, MapPin,
  ClipboardList, CheckCircle2, XCircle, Send, User, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const STATUS_META = {
  quote_requested:    { label: "New request",    tone: "bg-cyan-50 text-cyan-800 border-cyan-200",       icon: Inbox },
  partner_notified:   { label: "Notified",       tone: "bg-blue-50 text-blue-800 border-blue-200",       icon: Send },
  customer_contacted: { label: "Contacted",      tone: "bg-indigo-50 text-indigo-800 border-indigo-200", icon: User },
  quote_sent:         { label: "Quote sent",     tone: "bg-amber-50 text-amber-800 border-amber-200",    icon: ClipboardList },
  accepted:           { label: "Accepted",       tone: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  declined:           { label: "Declined",       tone: "bg-slate-100 text-slate-700 border-slate-200",   icon: XCircle },
  completed:          { label: "Completed",      tone: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: CheckCircle2 },
  closed:             { label: "Closed — no quote required", tone: "bg-slate-200 text-slate-700 border-slate-300", icon: XCircle },
};
const STATUS_ORDER = ["quote_requested","partner_notified","customer_contacted","quote_sent","accepted","declined","completed","closed"];

const ALLOCATION_META = {
  referral:            { label: "Referral row",       tone: "bg-emerald-50 text-emerald-800" },
  stored_attribution:  { label: "Stored attribution", tone: "bg-emerald-50 text-emerald-800" },
  auto_attribution:    { label: "Auto match",         tone: "bg-blue-50 text-blue-800" },
  postcode_match:      { label: "Postcode match",     tone: "bg-blue-50 text-blue-800" },
  admin_assigned:      { label: "Admin assigned",     tone: "bg-purple-50 text-purple-800" },
  unallocated:         { label: "Unallocated",        tone: "bg-rose-50 text-rose-800" },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-AU") : "—";
const daysOld = (iso) => {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

function StatusPill({ status, small=false }) {
  const meta = STATUS_META[status] || { label: status, tone: "bg-slate-100 text-slate-700 border-slate-200", icon: Inbox };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 border font-medium rounded-full ${meta.tone} ${small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`}>
      <Icon className={small ? "w-3 h-3" : "w-3.5 h-3.5"} /> {meta.label}
    </span>
  );
}

function AllocationPill({ source }) {
  const meta = ALLOCATION_META[source] || { label: source || "—", tone: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

const REFERRAL_META = {
  partner_referral_assigned:          { label: "Assigned",                tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  partner_referral_awaiting_contact:  { label: "Awaiting Contact",        tone: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  partner_referral_needs_admin_review:{ label: "Needs Admin Review",      tone: "bg-amber-50 text-amber-900 border-amber-200" },
};

/** Jun 2026 — Health Check partner-referral leads live in this same console. */
function ReferralBadges({ lead }) {
  if (lead.source !== "tank_water_health_check_referral") return null;
  const meta = REFERRAL_META[lead.referral_status];
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`admin-lead-referral-badges-${lead.id}`}>
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-900 border border-blue-200">
        Health Check Referral
      </span>
      {meta && (
        <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.tone}`}>
          {meta.label}
        </span>
      )}
      {lead.suggested_partner_snapshot?.business_name && !lead.partner_id && (
        <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
          Suggested: {lead.suggested_partner_snapshot.business_name}
          {lead.suggested_partner_distance_km != null ? ` · ${lead.suggested_partner_distance_km} km` : ""}
        </span>
      )}
    </div>
  );
}

export default function AdminQuoteLeadsPage() {
  useSEO({ title: "Quote leads — Admin", noindex: true, includeBaseline: false });
  const [leads, setLeads] = useState([]);
  const [openReview, setOpenReview] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterReview, setFilterReview] = useState(false);
  const [filterRegion, setFilterRegion] = useState("");
  // Jun 2026 — deep-linkable source filter (the Health Check admin tile
  // links here with ?source=tank_water_health_check_referral).
  const [filterSource, setFilterSource] = useState(
    () => new URLSearchParams(window.location.search).get("source") || "");
  const [regions, setRegions] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterReview) params.set("needs_review", "true");
      if (filterRegion) params.set("region", filterRegion);
      if (filterSource) params.set("source", filterSource);
      const { data } = await api.get(`/admin/quote-leads?${params.toString()}`);
      setLeads(data.leads || []);
      setOpenReview(data.open_review_count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterReview, filterRegion, filterSource]);

  useEffect(() => { load(); }, [load]);

  // Region list loaded once — never blocks the leads render.
  useEffect(() => {
    api.get("/admin/quote-leads/meta/regions")
      .then(({ data }) => setRegions(data.regions || []))
      .catch(() => setRegions([]));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return leads;
    const q = search.trim().toLowerCase();
    return leads.filter((l) =>
      (l.customer_name || "").toLowerCase().includes(q) ||
      (l.customer_email || "").toLowerCase().includes(q) ||
      (l.property_address || "").toLowerCase().includes(q) ||
      (l.proposal_reference || "").toLowerCase().includes(q) ||
      (l.partner_snapshot?.business_name || "").toLowerCase().includes(q),
    );
  }, [leads, search]);

  const counts = useMemo(() => {
    const map = { total: leads.length };
    for (const s of STATUS_ORDER) map[s] = 0;
    for (const l of leads) map[l.status] = (map[l.status] || 0) + 1;
    return map;
  }, [leads]);

  const exportCsv = () => {
    const cols = ["id","created_at","status","customer_name","customer_email","customer_phone","property_address","package_name","partner_business","partner_email","partner_region","allocation_source","needs_admin_review","estimated_value_cents","region_filter_applied"];
    const rows = [cols.join(",")];
    for (const l of filtered) {
      const snap = l.partner_snapshot || {};
      rows.push([
        l.id, l.created_at, l.status,
        JSON.stringify(l.customer_name || ""),
        JSON.stringify(l.customer_email || ""),
        JSON.stringify(l.customer_phone || ""),
        JSON.stringify(l.property_address || ""),
        JSON.stringify(l.package_name || ""),
        JSON.stringify(snap.business_name || ""),
        JSON.stringify(snap.email || ""),
        JSON.stringify(snap.franchise_region || ""),
        l.allocation_source || "",
        l.needs_admin_review ? "true" : "false",
        l.estimated_value_cents || "",
        JSON.stringify(filterRegion || "(all)"),
      ].join(","));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const regionSuffix = filterRegion === "__unallocated__" ? "-unallocated"
      : filterRegion ? `-${filterRegion.toLowerCase().replace(/\s+/g,"-")}`
      : "";
    a.download = `quote-leads${regionSuffix}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const selected = useMemo(() => leads.find((l) => l.id === selectedId), [leads, selectedId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="admin-quote-leads-page">
        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          data-testid="admin-quote-leads-back"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Partner Network
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Partner Network</div>
            <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">Quote leads console</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Every customer quote request across the network. Reassign partners, resolve unallocated
              enquiries, and monitor lifecycle progression. AWARE tracks referral status only — partners
              own their own pricing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {openReview > 0 && (
              <span
                data-testid="admin-quote-leads-review-count"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-800 bg-rose-50 border border-rose-200 rounded-full px-3 py-1.5"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> {openReview} needs review
              </span>
            )}
            <Button variant="outline" onClick={exportCsv} data-testid="admin-quote-leads-export" className="text-xs">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button variant="outline" onClick={load} data-testid="admin-quote-leads-refresh" className="text-xs">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" /> {error}
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <button
            onClick={() => { setFilterStatus(""); setFilterReview(false); }}
            data-testid="admin-quote-leads-filter-all"
            className={`text-left rounded-xl border p-3 transition-all ${!filterStatus && !filterReview ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}
          >
            <div className={`text-[10px] uppercase tracking-wider font-semibold ${!filterStatus && !filterReview ? "text-slate-300" : "text-slate-500"}`}>All</div>
            <div className="text-2xl font-bold mt-1">{counts.total}</div>
          </button>
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              onClick={() => { setFilterStatus(s); setFilterReview(false); }}
              data-testid={`admin-quote-leads-filter-${s}`}
              className={`text-left rounded-xl border p-3 transition-all ${filterStatus === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}
            >
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${filterStatus === s ? "text-slate-300" : "text-slate-500"}`}>{STATUS_META[s].label}</div>
              <div className="text-2xl font-bold mt-1">{counts[s] || 0}</div>
            </button>
          ))}
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="admin-quote-leads-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by customer, partner, reference or address…"
              className="pl-9"
            />
          </div>
          <select
            data-testid="admin-quote-leads-region-select"
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm min-w-[180px]"
          >
            <option value="">All regions</option>
            <option value="__unallocated__">Unallocated only</option>
            {regions.length > 0 && <option disabled>──────────</option>}
            {regions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select
            data-testid="admin-quote-leads-source-select"
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm min-w-[190px]"
          >
            <option value="">All lead sources</option>
            <option value="tank_water_health_check_referral">Health Check referrals</option>
            <option value="partner_landing_page">Partner landing pages</option>
          </select>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-quote-leads-review-toggle"
              checked={filterReview}
              onChange={(e) => { setFilterReview(e.target.checked); if (e.target.checked) setFilterStatus(""); }}
              className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            Needs review only
          </label>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading leads…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="admin-quote-leads-empty">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No leads match the current filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Property</th>
                  <th className="px-4 py-2">Partner</th>
                  <th className="px-4 py-2">Allocation</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Age</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((lead) => {
                  const age = daysOld(lead.created_at);
                  const aged = age > 3 && !["accepted","declined","completed"].includes(lead.status);
                  return (
                    <tr
                      key={lead.id}
                      data-testid={`admin-quote-lead-row-${lead.id}`}
                      onClick={() => setSelectedId(lead.id)}
                      className="hover:bg-slate-50 cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{lead.customer_name || "—"}</div>
                        <div className="text-[11px] text-slate-500">{lead.customer_email || "—"}</div>
                        <ReferralBadges lead={lead} />
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{lead.property_address || "—"}</span>
                        {lead.matched_service_region || lead.service_region_status ? (
                          <div className="text-[11px] text-slate-500">
                            {lead.matched_service_region
                              || (lead.service_region_status === "partner_referral"
                                ? "Outside direct service region"
                                : "Service region unresolved")}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        {lead.partner_snapshot?.business_name ? (
                          <>
                            <div className="text-slate-900 font-medium">{lead.partner_snapshot.business_name}</div>
                            <div className="text-[11px] text-slate-500">{lead.partner_snapshot.franchise_region || ""}</div>
                          </>
                        ) : (
                          <span className="text-rose-700 font-semibold text-xs inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Unallocated
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3"><AllocationPill source={lead.allocation_source} /></td>
                      <td className="px-4 py-3"><StatusPill status={lead.status} small /></td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${aged ? "text-rose-700 font-semibold" : "text-slate-500"}`}>
                          {age}d
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      {selected && (
        <AdminLeadDrawer
          lead={selected}
          onClose={() => setSelectedId(null)}
          onSaved={(updated) => {
            setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
            load();
          }}
        />
      )}
      </div>
    </div>
  );
}

// ── Admin drawer ──────────────────────────────────────────────────
function AdminLeadDrawer({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState(lead.status);
  const [reassignPartnerId, setReassignPartnerId] = useState("");
  const [note, setNote] = useState("");
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [markOutside, setMarkOutside] = useState(false);

  useEffect(() => {
    api.get("/admin/partners?partner_type=pws_franchisee&limit=200")
      .then(({ data }) => setPartners(Array.isArray(data) ? data : (data.partners || [])))
      .catch(() => setPartners([]));
  }, []);

  const doAssign = async () => {
    if (!reassignPartnerId) { toast.error("Choose a partner first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/quote-leads/${lead.id}/assign`, {
        partner_id: reassignPartnerId,
        note: note || null,
      });
      toast.success("Partner assigned — franchisee has been notified.");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign partner.");
    } finally {
      setBusy(false);
    }
  };

  const doStatusUpdate = async () => {
    setBusy(true);
    try {
      const payload = { status, note: note || null };
      if (markOutside) {
        payload.partner_notes = "MARKED OUTSIDE SERVICE AREA by admin";
        payload.quote_outcome = "outside_service_area";
      }
      const { data } = await api.patch(`/admin/quote-leads/${lead.id}`, payload);
      toast.success("Lead updated");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update lead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-quote-lead-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Quote lead</div>
            <h2 className="text-lg font-semibold text-slate-900 truncate">{lead.customer_name || "Unknown"}</h2>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <StatusPill status={lead.status} small />
              <AllocationPill source={lead.allocation_source} />
            </div>
          </div>
          <button data-testid="admin-quote-lead-drawer-close" onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Details */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1 text-sm">
            <div><b>Email:</b> {lead.customer_email || "—"}</div>
            <div><b>Phone:</b> {lead.customer_phone || "—"}</div>
            <div><b>Address:</b> {lead.property_address || "—"}</div>
            <div><b>Package:</b> {lead.package_name || "—"}</div>
            <div><b>Reference:</b> {lead.proposal_reference || lead.id}</div>
            <div className="text-xs text-slate-500 pt-1"><b>Allocation:</b> {lead.allocation_notes || "—"}</div>
          </section>

          {/* Allocated partner */}
          <section>
            <h3 className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-2">
              <Users className="w-4 h-4 text-cyan-600" /> Allocated partner
            </h3>
            {lead.partner_snapshot?.business_name ? (
              <div className="rounded-xl border border-slate-200 p-3 text-sm">
                <div className="font-medium">{lead.partner_snapshot.business_name}</div>
                <div className="text-xs text-slate-500">{lead.partner_snapshot.franchise_region} · {lead.partner_snapshot.email}</div>
              </div>
            ) : (
              <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm">
                No partner allocated yet — assign one below.
              </div>
            )}
          </section>

          {/* Reassign / assign partner */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900">Assign / reassign partner</h3>
            <select
              data-testid="admin-quote-lead-partner-select"
              value={reassignPartnerId}
              onChange={(e) => setReassignPartnerId(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              <option value="">Select a franchisee…</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>{p.business_name || p.email} · {p.franchise_region || p.state || ""}</option>
              ))}
            </select>
            <Input
              data-testid="admin-quote-lead-drawer-note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder="Reason / note (optional)"
            />
            <Button
              data-testid="admin-quote-lead-assign-btn"
              onClick={doAssign}
              disabled={busy || !reassignPartnerId}
              className="bg-slate-900 hover:bg-cyan-700 text-white"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign & notify partner
            </Button>
          </section>

          {/* Status change */}
          <section className="space-y-2 pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Status / admin override</h3>
            <select
              data-testid="admin-quote-lead-status-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{STATUS_META[s].label}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                data-testid="admin-quote-lead-mark-outside"
                checked={markOutside}
                onChange={(e) => setMarkOutside(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              />
              Mark outside service area (flag on outcome)
            </label>
            <Button
              data-testid="admin-quote-lead-save-btn"
              variant="outline"
              onClick={doStatusUpdate}
              disabled={busy}
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save status change
            </Button>
            {!["closed", "declined", "completed"].includes(lead.status) && (
              <Button
                data-testid="admin-quote-lead-close-btn"
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 ml-2"
                disabled={busy}
                onClick={async () => {
                  const reason = window.prompt("Close this lead — optional reason (e.g. customer decided not to proceed):", "");
                  if (reason === null) return;
                  setBusy(true);
                  try {
                    const { data } = await api.patch(`/admin/quote-leads/${lead.id}`, {
                      status: "closed", note: reason || "No quote required",
                    });
                    toast.success("Lead closed — removed from outstanding quote reminders");
                    onSaved(data);
                    onClose();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to close lead.");
                  } finally { setBusy(false); }
                }}
              >
                Close lead / no quote required
              </Button>
            )}
          </section>

          {/* Audit history */}
          {(lead.status_history || []).length > 0 && (
            <section className="pt-4 border-t border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Audit history</h3>
              <ol className="space-y-2 text-xs">
                {lead.status_history.slice().reverse().map((h, i) => (
                  <li key={i} className="border-l-2 border-slate-200 pl-3 py-1">
                    <div className="flex items-center gap-2">
                      <StatusPill status={h.status} small />
                      <span className="text-slate-500">{fmtDateTime(h.at)}</span>
                    </div>
                    {h.note && <div className="text-slate-700 mt-0.5">{h.note}</div>}
                    <div className="text-slate-400 mt-0.5">{h.role} · {h.by}</div>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
